import classNames from "classnames";
import AnimatedMetric from "./shared/AnimatedMetric";

type BoxStatsProps = {
	children?: React.ReactNode;
	icon?: React.ReactNode;
	styles?: string;
	title: string;
	stats: string | number;
	formatNumber?: (value: number) => string;
	compareStats?: string | number | null;
	compareLabel?: string;
	primaryLabel?: string;
	metricClassName?: string;
};

export const BoxStats = ({
	title,
	stats,
	icon,
	styles,
	formatNumber,
	compareStats,
	compareLabel = 'Compare',
	primaryLabel = 'Current',
	metricClassName = "text-[5.25rem]",
}: BoxStatsProps) => {
	const numericStats = typeof stats === "number" ? stats : Number(stats);
	const isNumeric = Number.isFinite(numericStats);
	const hasCompare = compareStats !== null && compareStats !== undefined;
	const numericCompare = typeof compareStats === "number" ? compareStats : Number(compareStats);
	const isCompareNumeric = hasCompare && Number.isFinite(numericCompare);
	const delta = isNumeric && isCompareNumeric ? numericStats - numericCompare : null;

	const formatValue = (value: number | string) => {
		if (typeof value === 'number' && Number.isFinite(value)) {
			return formatNumber ? formatNumber(value) : value.toLocaleString();
		}
		return String(value);
	};

	return (
		<div className="h-full w-full flex flex-col justify-between">
			<div className="flex items-center justify-start gap-2 text-left self-start">
				{icon ? (
					<div
						className={classNames(
							"rounded-xl h-10 w-10 md:h-11 md:w-11 flex items-center justify-center border border-white/60 dark:border-white/10 shrink-0",
							styles,
						)}
					>
						{icon}
					</div>
				) : null}
				<span className="text-sm md:text-base font-medium tracking-wide text-gray-600 dark:text-gray-300">{title}</span>
			</div>

			<div className="min-h-[88px] flex items-end justify-center text-center w-full">
					{isNumeric ? (
						<AnimatedMetric
							value={numericStats}
							formatter={formatNumber}
							className={`${metricClassName} leading-[0.95] font-semibold tracking-tight text-gray-900 dark:text-gray-100 whitespace-nowrap text-center`}
						/>
					) : (
						<span className={`${metricClassName} leading-[0.95] font-semibold tracking-tight text-gray-900 dark:text-gray-100 whitespace-nowrap text-center`}>{stats}</span>
					)}
			</div>

			{hasCompare && (
				<div className="mt-2 w-full rounded-xl border border-white/60 dark:border-white/10 bg-white/50 dark:bg-slate-900/40 px-3 py-2">
					<div className="flex items-center justify-between text-[11px] text-gray-600 dark:text-gray-300">
						<span className="uppercase tracking-[0.12em]">{primaryLabel}</span>
						<span className="font-semibold text-gray-800 dark:text-gray-100">{formatValue(stats)}</span>
					</div>
					<div className="mt-1 flex items-center justify-between text-[11px] text-gray-600 dark:text-gray-300">
						<span className="uppercase tracking-[0.12em]">{compareLabel}</span>
						<span className="font-semibold text-gray-800 dark:text-gray-100">{formatValue(compareStats as string | number)}</span>
					</div>
					{delta !== null && (
						<div className="mt-1 flex items-center justify-between text-[11px]">
							<span className="uppercase tracking-[0.12em] text-gray-500 dark:text-gray-400">Change</span>
							<span className={classNames(
								'font-semibold',
								delta > 0 ? 'text-emerald-600 dark:text-emerald-300' : delta < 0 ? 'text-rose-600 dark:text-rose-300' : 'text-gray-600 dark:text-gray-300',
							)}>
								{delta > 0 ? '+' : ''}{formatValue(delta)}
							</span>
						</div>
					)}
				</div>
			)}
		</div>
	);
};
